/**
 * Browser session helpers — HttpOnly cookie auth (PL-10 / PL-09).
 * Tokens must never be stored in localStorage or sessionStorage.
 */

import { devLog } from './devLog';
import {
  ensureCsrfToken,
  getCsrfHeaders,
  syncCsrfTokenFromCookie,
  rebootstrapCsrfToken,
  isCsrfValidationFailedResponse,
  UNSAFE_METHODS,
} from './csrf';
import { buildApiUrl } from './apiBaseUrl';

const LEGACY_AUTH_TOKEN_KEY = 'authToken';

/** Milliseconds before access expiry to proactively refresh. */
export const SESSION_REFRESH_LEAD_MS = 5 * 60 * 1000;

let refreshInFlight = null;

/**
 * Fetch auth session endpoints (429 responses are returned to callers for modal enforcement).
 * @param {string} endpoint
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function fetchAuthEndpoint(endpoint, options = {}) {
  const url = buildApiUrl(endpoint);
  const method = options.method || 'GET';
  const { headers: optionHeaders = {}, ...restOptions } = options;

  if (UNSAFE_METHODS.has(String(method).toUpperCase())) {
    await ensureCsrfToken();
  }

  const init = {
    credentials: 'include',
    ...restOptions,
    method,
    headers: {
      Accept: 'application/json',
      ...getCsrfHeaders(method),
      ...optionHeaders,
    },
  };

  return fetch(url, init);
}

/**
 * Remove legacy auth token from browser storage (migration from URL/localStorage flow).
 */
export function purgeLegacyAuthStorage() {
  try {
    localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
  } catch {
    /* private browsing / blocked storage */
  }
}

/**
 * Fetch session metadata without exposing JWT to JavaScript.
 * @returns {Promise<{ authenticated: boolean, expiresAt: string | null, refreshSupported: boolean, canRefresh: boolean }>}
 */
export async function fetchSessionStatus() {
  const response = await fetchAuthEndpoint('/api/auth/session', { method: 'GET' });

  if (!response.ok) {
    return { authenticated: false, expiresAt: null, refreshSupported: false, canRefresh: false };
  }

  const result = await response.json();
  return (
    result.data || {
      authenticated: false,
      expiresAt: null,
      refreshSupported: false,
      canRefresh: false,
    }
  );
}

/**
 * @typedef {'csrf' | 'unauthorized' | 'other'} RefreshFailureReason
 */

/**
 * @typedef {{ ok: true } | { ok: false, reason?: RefreshFailureReason, csrfRejected?: boolean }} RefreshSessionResult
 */

/**
 * Rotate access token via HttpOnly refresh cookie.
 * @returns {Promise<RefreshSessionResult>}
 */
export async function refreshSession() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const postRefresh = () => fetchAuthEndpoint('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      let response = await postRefresh();

      if (response.status === 403) {
        let body = {};
        try {
          body = await response.clone().json();
        } catch {
          /* non-JSON 403 */
        }

        if (isCsrfValidationFailedResponse(response.status, body)) {
          devLog.warn('authSession: refresh CSRF rejected, re-bootstrapping token');
          const bootstrapped = await rebootstrapCsrfToken();
          if (bootstrapped) {
            syncCsrfTokenFromCookie();
            response = await postRefresh();
          }
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          devLog.warn('authSession: refresh unauthorized', { status: response.status });
          return { ok: false, reason: 'unauthorized' };
        }
        if (response.status === 403) {
          let body = {};
          try {
            body = await response.clone().json();
          } catch {
            /* non-JSON 403 */
          }
          if (isCsrfValidationFailedResponse(response.status, body)) {
            devLog.warn('authSession: refresh failed after CSRF handling', { status: response.status });
            return { ok: false, reason: 'csrf', csrfRejected: true };
          }
        }
        devLog.warn('authSession: refresh failed', { status: response.status });
        return { ok: false, reason: 'other' };
      }

      const result = await response.json();
      if (result.success) {
        syncCsrfTokenFromCookie();
      }
      return { ok: Boolean(result.success) };
    } catch (error) {
      devLog.warn('authSession: refresh network error', error?.message || 'unknown');
      return { ok: false, reason: 'other' };
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Schedule proactive refresh before access token expiry.
 * @param {string | null} expiresAt ISO timestamp
 * @param {() => void | Promise<void>} onRefresh
 * @param {number} [leadMs=SESSION_REFRESH_LEAD_MS]
 * @returns {() => void} cleanup
 */
export function scheduleProactiveRefresh(expiresAt, onRefresh, leadMs = SESSION_REFRESH_LEAD_MS) {
  if (!expiresAt || typeof onRefresh !== 'function') {
    return () => {};
  }

  const expiresMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiresMs)) {
    return () => {};
  }

  const delayMs = Math.max(expiresMs - Date.now() - leadMs, 0);
  const timer = setTimeout(() => {
    Promise.resolve(onRefresh()).catch(() => {});
  }, delayMs);
  return () => clearTimeout(timer);
}

/**
 * @deprecated Use scheduleProactiveRefresh — kept for compatibility.
 */
export function scheduleSessionExpiryCheck(expiresAt, onExpired) {
  return scheduleProactiveRefresh(expiresAt, onExpired, 0);
}
